<template>
    <div class="fb-panel">
        <div class="fb-panel-head">
            <FileBrowserPath :breadcrumbItems="$store.state.breadcrumbs" />
        </div>
        <div class="fb-panel-body">
            <div v-if="loading" class="fb-empty">
                <div class="spinner-border text-muted" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>
            <div v-else-if="error" class="fb-notice fb-notice-danger">
                <i class="fa-solid fa-circle-exclamation"></i>
                <span>{{ error }}</span>
            </div>
            <div v-else class="fb-file">
                <div class="fb-file-glyph">
                    <i class="fa-solid" :class="[glyph.icon, glyph.tone]"></i>
                </div>
                <div class="fb-file-detail">
                    <h4 class="fb-file-name">{{ filename }}</h4>
                    <div class="fb-file-meta">
                        <span v-if="fileSize"><span class="fb-file-meta-label">Size</span>{{ fileSize }}</span>
                        <span v-if="fileTime"><span class="fb-file-meta-label">Modified</span>{{ fileTime }}</span>
                    </div>
                    <div class="fb-file-actions">
                        <button class="btn btn-sm btn-primary" :disabled="isDownloading || !downloadLink"
                            @click="downloadFile(downloadLink, filename)">
                            <span v-if="isDownloading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            <i v-else class="fa-solid fa-download me-2"></i>
                            {{ isDownloading ? 'Downloading…' : 'Download' }}
                        </button>
                        <button v-if="showOutputParser" class="btn btn-sm btn-outline-secondary" :disabled="tooLargeToParse"
                            :title="parseDisabledReason" @click="showPreview">
                            <i class="fa-solid fa-eye me-2"></i>Preview
                        </button>
                    </div>
                    <p v-if="showOutputParser && tooLargeToParse" class="fb-file-note">
                        <i class="fa-solid fa-circle-info me-1"></i>{{ parseDisabledReason }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapActions } from "vuex";
import FileBrowserPath from "../../components/filebrowser/FileBrowserPath.vue";
import { DateTimeFormat, formatBytes } from '../../utils/commonFunction'
import { MAX_PARSABLE_FILE_SIZE, fileGlyph, isParsableFile, parseStoragePath } from '../../utils/fileBrowser'
import axios from 'axios';
export default {
    name: "FileView",
    components: { FileBrowserPath },
    data() {
        return {
            loading: true,
            isDownloading: false,
            downloadLink: "",
            error: null,
            filename: '',
            fileSize: '',
            fileTime: '',
            fileBytes: 0
        }
    },
    beforeMount() {
        this.fetchFileData();
        if (this.$route.params.data) {
            let data = JSON.parse(this.$route.params.data)
            this.fileBytes = data.size ? data.size : 0;
            this.fileSize = this.fileBytes ? formatBytes(this.fileBytes) : '';
            this.fileTime = data.time ? DateTimeFormat(data.time) : '';
        }
    },
    computed: {
        // folderPath carries the trailing slash of a folder; a file path has none
        filePath() {
            return this.$store.state.amdp.folderPath.slice(0, -1);
        },
        credential() {
            return this.$store.state.amdp.selectedCredential;
        },
        glyph() {
            return fileGlyph(this.filename, false);
        },
        showOutputParser() {
            return isParsableFile(this.filename)
        },
        // parsing streams the whole object through pail, so large files are blocked
        tooLargeToParse() {
            return this.fileBytes > MAX_PARSABLE_FILE_SIZE
        },
        parseDisabledReason() {
            return this.tooLargeToParse
                ? `Preview is limited to files under ${MAX_PARSABLE_FILE_SIZE / (1024 * 1024)}MB.`
                : ''
        }
    },
    methods: {
        ...mapActions({
            showModal: "showModal",
            getPresignURL: "amdp/getPresignURL",
        }),
        showPreview() {
            this.showModal({
                component: 'CustomComponent',
                data: {
                    component: 'OutputParser',
                    path: this.filePath,
                    credential: this.credential,
                    size: this.fileBytes,
                    limit: 5,
                    filename: this.filename,
                    screenlarge: true,
                    title: 'File Preview'
                }
            });
        },
        async fetchFileData() {
            const parsed = parseStoragePath(this.filePath);
            this.filename = parsed?.key ? parsed.key.split("/").slice(-1)[0] : "";
            if (!this.filename) {
                // reached without a resolvable file path - fall back to the bucket listing
                this.$router.push({
                    name: 'File Browser List',
                    params: {
                        bucket: this.$route.params.bucket,
                        cid: this.$store.state.currentClient.cid
                    },
                    query: this.$route.query
                })
                this.loading = false
                return
            }
            try {
                const response = await this.getPresignURL({
                    credential: this.credential,
                    bucket: parsed.bucket,
                    key: parsed.key,
                });
                this.downloadLink = response.data?.url;
            } catch (error) {
                this.error = error.response?.data?.error || error.message;
            }
            this.loading = false
        },
        async downloadFile(url, label) {
            this.isDownloading = true;
            try {
                const response = await axios.get(url, { responseType: 'blob' });
                const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
                const a = document.createElement("a");
                a.href = objectUrl;
                a.download = label;
                a.click();
                window.URL.revokeObjectURL(objectUrl);
            } catch (error) {
                this.error = `Could not download ${label}: ${error.message}`;
            }
            this.isDownloading = false;
        }
    },
}
</script>
<style scoped>
.fb-panel-body {
    padding: 1.5rem;
}

.fb-file {
    display: flex;
    align-items: flex-start;
    gap: 1.25rem;
}

/* A tinted plate behind the type glyph, so the file reads as an object not a row */
.fb-file-glyph {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    border-radius: var(--fb-radius-md);
    background: var(--fb-surface-2);
    border: 1px solid var(--fb-border);
    font-size: 1.5rem;
}

.fb-file-detail {
    min-width: 0;
}

.fb-file-name {
    margin-bottom: 0.4rem;
    color: var(--fb-text);
    font-size: 17px;
    font-weight: 700;
    word-break: break-all;
}

.fb-file-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;
    margin-bottom: 1.25rem;
    font-size: 12px;
    color: var(--fb-text);
}

.fb-file-meta .fb-file-meta-label {
    margin-right: 0.4rem;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--fb-text-muted);
}

.fb-file-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

/* Sized against the 13px body of the rest of the browser rather than Bootstrap's
   default, which dwarfed the metadata beside it */
.fb-file-actions .btn {
    font-size: 13px;
    padding: 0.4rem 0.85rem;
    border-radius: var(--fb-radius-sm);
}

.fb-file-note {
    margin: 0.75rem 0 0;
    font-size: 12px;
    color: var(--fb-text-muted);
}
</style>
